import React from "react";
import { MetadataStrip } from "@/components/primitives";
import { SCHEMA_VERSION } from "@/data/schema";
import { cn } from "@/lib/utils";

/**
 * Container width vocabulary:
 *   "default" — max-w-5xl  (~1024px). Hub pages, forms, short-form.
 *   "wide"    — max-w-6xl  (~1152px). Long-form reading (papers, philosophy).
 *   "full"    — max-w-7xl  (~1280px). Content paired with diagrams or wide tables.
 *
 * The subtitle remains constrained at max-w-3xl regardless of shell width —
 * editorial hierarchy holds even when the body opens up.
 */
export type PageShellWidth = "default" | "wide" | "full";

const WIDTH_CLASS: Record<PageShellWidth, string> = {
  default: "max-w-5xl",
  wide: "max-w-6xl",
  full: "max-w-7xl",
};

export function PageShell({
  title,
  subtitle,
  children,
  stewardship = "Stewarded by the .person Foundation",
  version = SCHEMA_VERSION,
  status = "draft",
  licence = "CC-BY-4.0",
  width = "default",
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  stewardship?: string;
  version?: string;
  status?: string;
  licence?: string;
  width?: PageShellWidth;
}) {
  return (
    <div
      className={cn(
        "flex flex-col w-full mx-auto px-6 md:px-10 py-12 md:py-16 animate-in fade-in duration-700",
        WIDTH_CLASS[width],
      )}
    >
      <MetadataStrip version={version} status={status} licence={licence} stewardship={stewardship} />
      <div className="flex-1 py-8 md:py-12">
        <h1 className="text-4xl md:text-5xl font-serif font-normal tracking-tight mb-4 text-ink">{title}</h1>
        {subtitle && <p className="text-xl text-ink-2 font-serif mb-10 max-w-3xl leading-relaxed">{subtitle}</p>}
        {children}
      </div>
    </div>
  );
}
